/**
 * Floating Aircall Workspace dock — bottom-right of every page.
 *
 * Important: the SDK iframe lives in a separate fixed-position container
 * appended to <body> by AircallProvider. THIS component renders ONLY the
 * chrome (header bar + collapsed pill). Both the chrome and the iframe
 * host share the same `pos` coords so they line up visually as one panel.
 *
 * We never reparent the iframe — browsers reload iframes when their
 * DOM parent changes, which kills the SDK's session and listeners.
 */
import { useRef } from "react";
import { Phone, PhoneOff, ChevronDown, ExternalLink, GripVertical, BellOff, Bell } from "lucide-react";
import { useAircall } from "@/contexts/AircallContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const HEADER_H = 56;

export default function AircallDock() {
  const ctx = useAircall();
  const dragOffset = useRef(null);

  if (!ctx) return null;
  const { expanded, expand, collapse, loggedIn, ready, lastCall, pos, setPos, dockWidth, dockHeight, muted, toggleMute } = ctx;

  // ── Drag the chrome — host follows via shared pos state ─────────────
  const onDragStart = (e) => {
    const startX = e.clientX;
    const startY = e.clientY;
    const startRight  = pos.right;
    const startBottom = pos.bottom;
    dragOffset.current = { startX, startY, startRight, startBottom };
    const move = (ev) => {
      const dx = ev.clientX - dragOffset.current.startX;
      const dy = ev.clientY - dragOffset.current.startY;
      const right  = Math.max(8, dragOffset.current.startRight  - dx);
      const bottom = Math.max(8, dragOffset.current.startBottom - dy);
      setPos({ right, bottom });
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      dragOffset.current = null;
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  // ── Collapsed: nothing floats — reopened from the sidebar launcher ──
  if (!expanded) {
    return null;
  }

  // ── Expanded: chrome header positioned RIGHT ABOVE the host iframe ──
  // The host iframe sits at (right=pos.right, bottom=pos.bottom, width=376, height=580).
  // The chrome sits directly on top of it at bottom = pos.bottom + dockHeight.
  return (
    <div
      data-testid="aircall-dock-panel"
      style={{
        position: "fixed",
        right:    pos.right,
        bottom:   pos.bottom + dockHeight,  // Sits flush above the iframe
        width:    dockWidth,
        height:   HEADER_H,
        zIndex:   9999,  // Above the host
      }}
      className="bg-slate-900 text-white rounded-t-xl shadow-2xl border border-slate-200 border-b-0 flex items-center justify-between gap-2 px-3"
    >
      <div
        className="flex items-center gap-2 min-w-0 flex-1 cursor-move select-none h-full"
        onMouseDown={onDragStart}
        data-testid="aircall-dock-header"
      >
        <GripVertical className="w-4 h-4 text-slate-400 shrink-0" />
        <Phone className="w-4 h-4 shrink-0" />
        <span className="text-sm font-medium truncate">Aircall</span>
        {loggedIn ? (
          <Badge variant="outline" className="text-[10px] h-5 bg-emerald-500/20 text-emerald-100 border-emerald-500/40">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 mr-1.5" />
            online
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] h-5 bg-amber-500/20 text-amber-100 border-amber-500/40">
            sign in below
          </Badge>
        )}
        {lastCall && (
          <span className="text-[10px] text-slate-300 truncate hidden sm:inline" data-testid="aircall-dock-last-call">
            · {lastCall.direction === "incoming" ? "🔔" : "→"} {lastCall.phone}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleMute}
          className={`h-7 w-7 hover:bg-slate-800 ${muted ? "text-amber-300 hover:text-amber-200" : "text-slate-300 hover:text-white"}`}
          title={muted ? "Aircall muted in this browser — click to unmute" : "Mute Aircall in this browser (calls will only ring in your desktop/mobile app)"}
          data-testid="aircall-dock-mute-toggle"
        >
          {muted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => window.open("https://workspace.aircall.io/", "_blank", "noopener")}
          className="h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800"
          title="Open Aircall in a new tab"
          data-testid="aircall-dock-popout"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={collapse}
          className="h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800"
          title="Collapse"
          data-testid="aircall-dock-collapse"
        >
          <ChevronDown className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
